import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../../Services/Context/LanguageContext";
import { useAuth } from "../../Services/Context/AuthContext";
import { SPREAD_CONFIGS } from "../../config/spreadConfigs";
import { saveUserPreference } from "../../Services/userPreferences";
import "./QuestionInput.css";

export type ReadingType = "single" | "yes-or-no" | "past-present-future" | "relationship" | "you-position-potential" | "unconscious-conscious-superconscious" | "embrace-accept-let-go" | "situation-obstacle-lesson";

// eslint-disable-next-line no-control-regex
const DANGEROUS_CHARS = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F<>{}[\]\\`]/g;

interface QuestionInputProps {
  onSubmit: (question: string, readingType: ReadingType) => void;
  onAboutClick: () => void;
  rateLimitError?: string | null;
}

const QuestionInput = ({ onSubmit, onAboutClick, rateLimitError }: QuestionInputProps) => {
  const { translate } = useLanguage();
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [readingType, setReadingType] = useState<ReadingType>(() => {
    try {
      return (localStorage.getItem("fatespin_reading_type") as ReadingType) ?? "single";
    } catch {
      return "single";
    }
  });
  const [showWarning, setShowWarning] = useState(false);
  const [isReadingTypeOpen, setIsReadingTypeOpen] = useState(false);
  const [readingMenuDropsUp, setReadingMenuDropsUp] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const readingTypeRef = useRef<HTMLDivElement>(null);
  const syncedUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const timer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  // Apply cloud reading type preference on login
  useEffect(() => {
    if (!user || user.id === syncedUserIdRef.current) return;
    syncedUserIdRef.current = user.id;
    const cloudType = user.user_metadata?.pref_reading_type as ReadingType | undefined;
    const validTypes: ReadingType[] = ["single", "yes-or-no", "past-present-future", "relationship", "you-position-potential", "unconscious-conscious-superconscious", "embrace-accept-let-go", "situation-obstacle-lesson"];
    if (cloudType && validTypes.includes(cloudType)) {
      setReadingType(cloudType);
      try { localStorage.setItem("fatespin_reading_type", cloudType); } catch {}
    }
  }, [user]);

  useEffect(() => {
    if (!isReadingTypeOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (readingTypeRef.current && !readingTypeRef.current.contains(e.target as Node)) {
        setIsReadingTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isReadingTypeOpen]);

  const trySubmit = () => {
    if (question.trim().length >= 3) {
      onSubmit(question.trim(), readingType);
    } else {
      setShowWarning(true);
      textareaRef.current?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    trySubmit();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      trySubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value.replace(DANGEROUS_CHARS, ""));
    if (showWarning) setShowWarning(false);
  };

  const selectReadingType = (type: ReadingType) => {
    setReadingType(type);
    try { localStorage.setItem("fatespin_reading_type", type); } catch {}
    if (user) saveUserPreference("pref_reading_type", type);
    setIsReadingTypeOpen(false);
  };

  const toggleReadingTypeMenu = () => {
    if (!isReadingTypeOpen && readingTypeRef.current) {
      const spaceBelow = window.innerHeight - readingTypeRef.current.getBoundingClientRect().bottom;
      setReadingMenuDropsUp(spaceBelow < 280);
    }
    setIsReadingTypeOpen((open) => !open);
  };

  const currentReadingTypeLabel =
    readingType === "single"
      ? translate("singleCard")
      : readingType === "yes-or-no"
        ? translate("yesOrNo")
        : translate(SPREAD_CONFIGS[readingType as keyof typeof SPREAD_CONFIGS].nameKey);

  return (
    <motion.div
      className="QuestionInput"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 1.2, ease: "easeOut" }}
    >
      <motion.div
        className="QuestionInput-container"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.div
          className="QuestionInput-sigil"
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        >
          ✦
        </motion.div>
        <h1 className="QuestionInput-title">{translate("questionTitle")}</h1>
        <p className="QuestionInput-subtitle">
          {translate("questionSubtitle")}
        </p>
        <form onSubmit={handleSubmit}>
          <textarea
            maxLength={user ? 2000 : 600}
            ref={textareaRef}
            className="QuestionInput-textarea"
            value={question}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={translate("questionPlaceholder")}
            rows={3}
          />
          <AnimatePresence>
            {rateLimitError ? (
              <motion.p
                key="rate-limit"
                className="QuestionInput-warning"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {rateLimitError}
              </motion.p>
            ) : showWarning ? (
              <motion.p
                key="question-warning"
                className="QuestionInput-warning"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {translate("questionWarning")}
              </motion.p>
            ) : null}
          </AnimatePresence>
          {user && (
            <div className="QuestionInput-reading-type">
              <span className="QuestionInput-reading-current">{currentReadingTypeLabel}</span>

              <div className="QuestionInput-reading-dropdown" ref={readingTypeRef}>
                <button
                  type="button"
                  className="QuestionInput-reading-trigger"
                  onClick={toggleReadingTypeMenu}
                  aria-haspopup="listbox"
                  aria-expanded={isReadingTypeOpen}
                >
                  {translate("readingType")}
                  <span
                    className={`QuestionInput-reading-chevron${isReadingTypeOpen ? " QuestionInput-reading-chevron--open" : ""}`}
                  />
                </button>

                {isReadingTypeOpen && (
                  <div
                    className={`QuestionInput-reading-menu-anchor${readingMenuDropsUp ? " QuestionInput-reading-menu-anchor--up" : ""}`}
                  >
                    <AnimatePresence>
                      <motion.div
                        className="QuestionInput-reading-menu"
                        role="listbox"
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18, ease: "easeOut" }}
                      >
                        <div className="QuestionInput-reading-menu-group">
                          <span className="QuestionInput-reading-menu-label">
                            {translate("singleCard")}
                          </span>
                          <button
                            type="button"
                            className={`QuestionInput-reading-option ${readingType === "single" ? "QuestionInput-reading-option--active" : ""}`}
                            onClick={() => selectReadingType("single")}
                          >
                            {translate("singleCard")}
                          </button>
                          <button
                            type="button"
                            className={`QuestionInput-reading-option ${readingType === "yes-or-no" ? "QuestionInput-reading-option--active" : ""}`}
                            onClick={() => selectReadingType("yes-or-no")}
                          >
                            {translate("yesOrNo")}
                          </button>
                        </div>

                        <div className="QuestionInput-reading-menu-group">
                          <span className="QuestionInput-reading-menu-label">
                            {translate("threeCardSpread")}
                          </span>
                          {Object.values(SPREAD_CONFIGS).map((spread) => (
                            <button
                              key={spread.type}
                              type="button"
                              className={`QuestionInput-reading-option ${readingType === spread.type ? "QuestionInput-reading-option--active" : ""}`}
                              onClick={() => selectReadingType(spread.type)}
                            >
                              {translate(spread.nameKey)}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                )}
              </div>
            </div>
          )}

          <motion.button
            className="QuestionInput-button"
            type="submit"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            {translate("drawCard")}
          </motion.button>
        </form>
        <button className="QuestionInput-about" onClick={onAboutClick}>
          {translate("aboutPrivacy")}
        </button>
      </motion.div>
    </motion.div>
  );
};

export default QuestionInput;
